import type { ReactElement } from 'react'
import { Navigate, Outlet, useParams } from 'react-router-dom'
import type { RouteObject } from 'react-router-dom'

import { RequireAuth } from './auth/RequireAuth'
import { RequireSection } from './auth/RequireSection'
import type { Section } from './auth/useSession'
import { AppShell } from './layout/AppShell'
import { LoginPage } from './pages/LoginPage'
import { ChangePasswordPage } from './pages/account/ChangePasswordPage'
import { ChangePinPage } from './pages/account/ChangePinPage'
import { AdminIndex, AdminLayout } from './pages/admin/AdminLayout'
import { AssignmentsPage } from './pages/assignments/AssignmentsPage'
import { BmHomologationsPage } from './pages/configuration/BmHomologationsPage'
import { ChillSlotsPage } from './pages/configuration/ChillSlotsPage'
import { ConfigChangesPage } from './pages/configuration/ConfigChangesPage'
import { ConfigStatusPage } from './pages/configuration/ConfigStatusPage'
import { MachineGroupsPage } from './pages/configuration/MachineGroupsPage'
import { MepMarginHeatPage } from './pages/configuration/MepMarginHeatPage'
import { MepSettingsPage } from './pages/configuration/MepSettingsPage'
import { MepSharedMachinesPage } from './pages/configuration/MepSharedMachinesPage'
import { MepStaffingPage } from './pages/configuration/MepStaffingPage'
import { PesajeSettingsPage } from './pages/configuration/PesajeSettingsPage'
import { PlanMachinesPage } from './pages/configuration/PlanMachinesPage'
import { PlannerSettingsPage } from './pages/configuration/PlannerSettingsPage'
import { QualityRulesPage } from './pages/configuration/QualityRulesPage'
import { RacionadoSettingsPage } from './pages/configuration/RacionadoSettingsPage'
import { SpecialSchedulePage } from './pages/configuration/SpecialSchedulePage'
import { GroupDetailPage } from './pages/groups/GroupDetailPage'
import { GroupsPage } from './pages/groups/GroupsPage'
import { HomePage } from './pages/home/HomePage'
import { OperationsPage } from './pages/operations/OperationsPage'
import { RolesPage } from './pages/roles/RolesPage'
import { UsersPage } from './pages/users/UsersPage'
import {
  ADMIN_PATH,
  ASSIGNMENTS_PATH,
  CHANGE_PASSWORD_PATH,
  CHANGE_PIN_PATH,
  CHILL_SLOTS_PATH,
  CONFIG_BM_HOMOLOGATIONS_PATH,
  CONFIG_CHANGES_PATH,
  CONFIG_MACHINE_GROUPS_PATH,
  CONFIG_PLAN_MACHINES_PATH,
  CONFIG_QUALITY_RULES_PATH,
  CONFIG_SETTINGS_PATH,
  CONFIG_SPECIAL_SCHEDULE_PATH,
  CONFIG_STATUS_PATH,
  GROUPS_PATH,
  GROUP_DETAIL_PATTERN,
  HOME_PATH,
  LEGACY_ASSIGNMENTS_PATH,
  LEGACY_GROUPS_PATH,
  LEGACY_GROUP_DETAIL_PATTERN,
  LEGACY_USERS_PATH,
  LOGIN_PATH,
  MEP_MARGIN_HEAT_PATH,
  MEP_SETTINGS_PATH,
  MEP_SHARED_MACHINES_PATH,
  MEP_STAFFING_PATH,
  OPERATIONS_PATH,
  PESAJE_SETTINGS_PATH,
  RACIONADO_SETTINGS_PATH,
  ROLES_PATH,
  USERS_PATH,
  groupDetailPath,
} from './lib/routes'

/**
 * One entry of the route table: what react-router needs to answer a URL, under a
 * name. Nothing here builds URLs — a component links with the constants and
 * builders in `lib/routes` and never reaches into this table.
 */
const routeEntry = (path: string, element: ReactElement) => ({
  routeProps: { path, element },
})

/** A URL from before the administration area, sent to where the screen lives now. */
const redirectEntry = (path: string, to: string) => routeEntry(path, <Navigate to={to} replace />)

/** `/grupos/:id` → `/admin/grupos/:id`: the one old URL with a param to carry over. */
const LegacyGroupDetailRedirect = (): ReactElement => {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={groupDetailPath(id ?? '')} replace />
}

/**
 * RoutesMap — the typed route table. Every path the SPA answers to is named here
 * once, paired with the element that answers it, and `appRoutes` below is the
 * only place the shape of the tree is written.
 *
 * The table is read for its `routeProps` and for nothing else: a page does not
 * import it to build its links. That would close a module cycle, and the
 * elements here are created at module scope, so the cycle would blow up rather
 * than resolve quietly.
 */
export const RoutesMap = {
  public: {
    login: routeEntry(LOGIN_PATH, <LoginPage />),
  },
  protected: {
    // Home needs no section: it shows the operations to whoever holds them and
    // says so to whoever does not, which is also what makes it a safe target for
    // every guard's bounce.
    home: routeEntry(HOME_PATH, <HomePage />),
    // Signed in is the whole requirement: everybody changes their own password,
    // so this belongs to no section.
    changePassword: routeEntry(CHANGE_PASSWORD_PATH, <ChangePasswordPage />),
    changePin: routeEntry(CHANGE_PIN_PATH, <ChangePinPage />),
  },
  // The URLs the management screens had before the administration area. Signed
  // in only: the screen they land on carries its own guard.
  legacy: {
    users: redirectEntry(LEGACY_USERS_PATH, USERS_PATH),
    groups: redirectEntry(LEGACY_GROUPS_PATH, GROUPS_PATH),
    groupDetail: routeEntry(LEGACY_GROUP_DETAIL_PATTERN, <LegacyGroupDetailRedirect />),
    assignments: redirectEntry(LEGACY_ASSIGNMENTS_PATH, ASSIGNMENTS_PATH),
  },
  // One entry per section, each behind its `RequireSection`. Every procedure
  // these screens talk to declares the same section in the API — that is what
  // protects them; the guard only keeps a person from staring at a screen that
  // answers FORBIDDEN to everything.
  operaciones: {
    operations: routeEntry(OPERATIONS_PATH, <OperationsPage />),
  },
  // The three below live inside the administration area (`/admin`), its
  // sidebar offering each group to whoever holds the section.
  admin: {
    users: routeEntry(USERS_PATH, <UsersPage />),
    roles: routeEntry(ROLES_PATH, <RolesPage />),
  },
  grupos: {
    groups: routeEntry(GROUPS_PATH, <GroupsPage />),
    groupDetail: routeEntry(GROUP_DETAIL_PATTERN, <GroupDetailPage />),
    assignments: routeEntry(ASSIGNMENTS_PATH, <AssignmentsPage />),
  },
  configuracion: {
    status: routeEntry(CONFIG_STATUS_PATH, <ConfigStatusPage />),
    machineGroups: routeEntry(CONFIG_MACHINE_GROUPS_PATH, <MachineGroupsPage />),
    planMachines: routeEntry(CONFIG_PLAN_MACHINES_PATH, <PlanMachinesPage />),
    qualityRules: routeEntry(CONFIG_QUALITY_RULES_PATH, <QualityRulesPage />),
    specialSchedule: routeEntry(CONFIG_SPECIAL_SCHEDULE_PATH, <SpecialSchedulePage />),
    bmHomologations: routeEntry(CONFIG_BM_HOMOLOGATIONS_PATH, <BmHomologationsPage />),
    settings: routeEntry(CONFIG_SETTINGS_PATH, <PlannerSettingsPage />),
    changes: routeEntry(CONFIG_CHANGES_PATH, <ConfigChangesPage />),
    mepStaffing: routeEntry(MEP_STAFFING_PATH, <MepStaffingPage />),
    mepSharedMachines: routeEntry(MEP_SHARED_MACHINES_PATH, <MepSharedMachinesPage />),
    mepMarginHeat: routeEntry(MEP_MARGIN_HEAT_PATH, <MepMarginHeatPage />),
    mepSettings: routeEntry(MEP_SETTINGS_PATH, <MepSettingsPage />),
    racionadoSettings: routeEntry(RACIONADO_SETTINGS_PATH, <RacionadoSettingsPage />),
    chillSlots: routeEntry(CHILL_SLOTS_PATH, <ChillSlotsPage />),
    pesajeSettings: routeEntry(PESAJE_SETTINGS_PATH, <PesajeSettingsPage />),
  },
} as const

/** One path-less `RequireSection` layout route around every screen of a section. */
const sectionRoutes = (
  section: Section,
  entries: Record<string, { routeProps: RouteObject }>,
): RouteObject => ({
  element: <RequireSection section={section}><Outlet /></RequireSection>,
  children: Object.values(entries).map((entry) => entry.routeProps),
})

/**
 * The route tree handed to `createBrowserRouter`.
 *
 * Nested guards, and each is a LAYOUT route rather than a wrapper repeated per
 * screen: `RequireAuth` + `AppShell` around everything signed in, and one
 * path-less `RequireSection` per section around its screens. A per-screen
 * wrapper is a decision taken once per screen, and the next screen is the one
 * that forgets it. The administration area is one more layout (`AdminLayout`,
 * the sidebar) holding three of those section groups, whose index sends `/admin`
 * to the first screen the session can see.
 */
export const appRoutes: Array<RouteObject> = [
  RoutesMap.public.login.routeProps,
  {
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      RoutesMap.protected.home.routeProps,
      RoutesMap.protected.changePassword.routeProps,
      RoutesMap.protected.changePin.routeProps,
      {
        element: <RequireSection section="operaciones"><Outlet /></RequireSection>,
        children: [RoutesMap.operaciones.operations.routeProps],
      },
      ...Object.values(RoutesMap.legacy).map((entry) => entry.routeProps),
      {
        path: ADMIN_PATH,
        element: <AdminLayout />,
        children: [
          { index: true, element: <AdminIndex /> },
          sectionRoutes('admin', RoutesMap.admin),
          sectionRoutes('grupos', RoutesMap.grupos),
          sectionRoutes('configuracion', RoutesMap.configuracion),
        ],
      },
    ],
  },
  // Catch-all: an unknown URL goes home, and the guard sends it to /login if the
  // session is gone.
  { path: '*', element: <Navigate to={HOME_PATH} replace /> },
]
